import { useState } from "react"
import { supabase } from "@/integrations/supabase/client"
import { toast } from "sonner"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Search, ChevronRight } from "lucide-react"

interface RegistrationResult {
  id: string
  class_name: string
  class_level: string
  school_name: string
}

interface SearchRegistrationProps {
  onRegistrationFound: (registrationId: string) => void
}

export const SearchRegistration = ({ onRegistrationFound }: SearchRegistrationProps) => {
  const [email, setEmail] = useState("")
  const [isSearching, setIsSearching] = useState(false)
  const [results, setResults] = useState<RegistrationResult[]>([])
  const [hasSearched, setHasSearched] = useState(false)

  const handleSearch = async () => {
    if (!email.trim()) {
      toast.error("Veuillez entrer un email")
      return
    }

    setIsSearching(true)
    setResults([])
    setHasSearched(false)
    
    try {
      const { data, error } = await supabase
        .from('contest_registrations')
        .select('id, class_name, class_level, school_name')
        .eq('teacher_email', email.trim())

      if (error) throw error

      setHasSearched(true)

      if (!data || data.length === 0) {
        toast.error("Aucune inscription trouvée pour cet email")
        return
      }

      if (data.length === 1) {
        toast.success("Inscription trouvée !")
        onRegistrationFound(data[0].id)
      } else {
        setResults(data)
        toast.success(`${data.length} inscriptions trouvées`)
      }
    } catch (error) {
      console.error('Error searching registration:', error)
      toast.error("Une erreur s'est produite lors de la recherche")
    } finally {
      setIsSearching(false)
    }
  }

  const handleSelectRegistration = (id: string) => {
    onRegistrationFound(id)
    setResults([])
    setHasSearched(false)
  }

  return (
    <Card className="mb-6 border-black/40">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Search className="h-5 w-5" />
          Rechercher / Modifier une inscription existante
        </CardTitle>
        <CardDescription>
          Entrez l'email de l'enseignant référent pour retrouver et modifier une inscription
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex gap-2">
          <Input
            type="email"
            placeholder="Email de l'enseignant référent"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            className="flex-1 border-black/40"
          />
          <Button 
            onClick={handleSearch}
            disabled={isSearching}
            className="min-w-[120px] bg-[#4AA9E6] hover:bg-[#3A99D6] text-white"
          >
            {isSearching ? "Recherche..." : "Rechercher"}
          </Button>
        </div>

        {results.length > 0 && (
          <div className="border border-black/40 rounded-lg divide-y divide-black/20">
            <div className="px-4 py-2 bg-muted/50 font-medium text-sm">
              Sélectionnez une classe :
            </div>
            {results.map((registration) => (
              <button
                key={registration.id}
                onClick={() => handleSelectRegistration(registration.id)}
                className="w-full px-4 py-3 flex items-center justify-between hover:bg-muted/50 transition-colors text-left"
              >
                <div>
                  <p className="font-medium">
                    {registration.class_name || registration.class_level}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {registration.school_name}
                  </p>
                </div>
                <ChevronRight className="h-5 w-5 text-muted-foreground" />
              </button>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  )
}